"use client";
// FilePicker.js
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import "@mantine/dropzone/styles.css";
import type { StatementUploadResponse } from "@/store/appStore";
import { ReactElement, useState, useEffect, useCallback } from "react";
// Removed react-query imports
import axios from "axios";
import { getAblyClient, releaseAblyClient } from "./ably";
import { Alert } from "./alert";
import { UserWithCompanies } from "@/components/auth";
import { useAppStore, selectUser } from "@/store/appStore";
import { Realtime, RealtimeChannel } from "ably";

interface UploadResponse {
  eventIds: [string] | undefined;
}

interface FilePickerProps {
  children: ReactElement;
  onUploadComplete?: (data: StatementUploadResponse) => void;
  uploadPath: string;
}

interface ProgressPayload {
  eventId: string;
  stepName: string;
  percent: number;
  error?: string;
  currentStep: number;
  totalSteps: number;
}

interface ProgressDisplay {
  eventId?: string;
  stepName?: string;
  percent?: number;
}

const FilePicker = ({
  children,
  onUploadComplete,
  uploadPath,
}: FilePickerProps) => {
  const user: UserWithCompanies | null = useAppStore(selectUser);

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  const [processingProgress, setProcessingProgress] = useState<
    Record<string, ProgressDisplay>
  >({});

  const [ably, setAbly] = useState<Realtime | undefined>();
  const [ablyChannel, setAblyChannel] = useState<RealtimeChannel | undefined>();
  const [activeJobs, setActiveJobs] = useState<
    Array<{ eventId: string; processing: boolean }>
  >([]);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertTitle, _setAlertTitle] = useState("Error uploading documents");
  const [alertDescription, setAlertDescription] = useState(
    "An error occurred while uploading documents"
  );

  // Cleanup Ably connection when component unmounts

  const disconnectAbly = useCallback(() => {
    console.log("FilePicker disconnectAbly");
    if (ablyChannel) {
      ablyChannel.unsubscribe();
    }
    releaseAblyClient();
  }, [ablyChannel]);

  useEffect(() => {
    return () => {
      console.log("FilePicker unmounted");
      disconnectAbly();
    };
  }, [disconnectAbly]);

  const uploadDocuments = async (
    files: FileWithPath[]
  ): Promise<UploadResponse[] | undefined> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("file", file, file.name);
    });

    try {
      const response = await axios.post(uploadPath, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          console.log("@progressEvent", progressEvent);
          const totalFilesSize = progressEvent.total;
          const uploadedFilesSize = progressEvent.loaded;
          const percentCompleted = Math.round(
            (uploadedFilesSize * 100) / totalFilesSize!
          );
          setUploadProgress({ overall: percentCompleted });
        },
      });

      console.info("response:", response);
      if (response.data.error) {
        console.error("Error uploading documents:", response);
        return undefined;
      }

      if (!response.data.eventIds) {
        console.error(
          "Error uploading documents eventIds are missing:",
          response
        );
        return undefined;
      }

      const ably = getAblyClient();
      console.log("setAbly Client", ably);
      setAbly(ably);
      setActiveJobs((prevJobs) => [
        ...prevJobs,
        ...response.data.eventIds.map((eventId: string) => ({
          eventId,
          processing: true,
        })),
      ]);
    } catch (error) {
      console.error("Error uploading documents:", error);
      setIsAlertOpen(true);
      disconnectAbly();
      setActiveJobs([]);
      throw error;
    }
  };

  const subscribeToProcessingUpdates = useCallback(
    (eventId: string) => {
      if (user && user.id && ably) {
        const channel = ably.channels.get(user.id);
        setAblyChannel(channel);
        console.log(
          "FilePicker subscribeToProcessingUpdates",
          `event-progress-${eventId}`
        );
        console.log(
          "FilePicker subscribeToProcessingUpdates",
          `channel`,
          channel
        );

        const onProgressUpdate = (
          message: Partial<{ data: ProgressPayload }>
        ) => {
          console.log("FilePicker onProgressUpdate", message);

          if (message && message.data && message.data.error) {
            setAlertDescription(message.data.error);
            setIsAlertOpen(true);
            disconnectAbly();
            setProcessingProgress((prevState) => ({
              ...prevState,
              [eventId]: {
                stepName: "Error",
                percent: 100,
              },
            }));
          } else if (message && message.data) {
            console.log(` ${eventId} PROGRESS`, message.data.percent + "%");
            setProcessingProgress((prevState) => ({
              ...prevState,
              [eventId]: {
                eventId: message.data?.eventId,
                stepName: message.data?.stepName,
                percent: message.data?.percent,
              },
            }));

            if (message.data.currentStep >= message.data.totalSteps) {
              // Processing finished for this job
              setActiveJobs((prevJobs) =>
                prevJobs.filter((job) => job.eventId !== eventId)
              );
              console.log("activeJobs.length", activeJobs.length);
              console.log("UPLOAD_PROCESSING_COMPLETE");
              // Last job finished; close Ably connection
              disconnectAbly();
            }
          }
        };

        channel.on((stateChange) => {
          console.log("Current Channel state: " + stateChange.current);
          console.log("Previous Channel state: " + stateChange.previous);
          if (stateChange.reason) {
            console.log(
              "the reason for the Channel state change was: " +
                stateChange.reason.toString()
            );
          }
        });

        channel.subscribe(`event-progress-${eventId}`, onProgressUpdate);
        channel.on("failed", (error) => {
          console.error("Channel error", error);
          setAlertDescription("An error occurred while processing documents");
          setIsAlertOpen(true);
          disconnectAbly();
        });
      }
    },
    [user, ably, disconnectAbly, activeJobs]
  );

  useEffect(() => {
    console.log("FilePicker activeJobs!!", activeJobs, ably);
    activeJobs.forEach(({ eventId }) => {
      subscribeToProcessingUpdates(eventId);
    });
  }, [ably, activeJobs, subscribeToProcessingUpdates]);

  const uploadDocumentsAction = useAppStore((s) => s.uploadDocuments);
  const [isPending, setIsPending] = useState(false);

  return (
    <div>
      <Dropzone
        loading={isPending}
        useFsAccessApi={false}
        onDrop={async (files) => {
          setIsPending(true);
          try {
            const data = await uploadDocumentsAction(files, uploadPath);
            if (onUploadComplete && data) {
              onUploadComplete(data);
            }
          } catch (err) {
            setIsAlertOpen(true);
          } finally {
            setIsPending(false);
          }
        }}
      >
        {children}
      </Dropzone>

      <Alert
        title={alertTitle}
        description={alertDescription}
        open={isAlertOpen}
        onContinue={() => setIsAlertOpen(false)}
      />

      {/* Upload Progress */}
      {isPending && uploadProgress.overall !== undefined && (
        <div>
          <p>Upload Progress: {uploadProgress.overall}%</p>
          <progress value={uploadProgress.overall} max="100" />
        </div>
      )}

      {/* Processing Progress */}
      {Object.keys(processingProgress).length > 0 && (
        <div>
          {Object.entries(processingProgress).map(([_key, event]) => (
            <div className="p-5 border m-5" key={event.eventId}>
              <b>{event.eventId}</b>{" "}
              {/**TODO: Replace this with an Event Name (documentName) */}
              <p>
                {event.stepName}: {event.percent}%
              </p>
              {event.percent && <progress value={event.percent} max="100" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { FilePicker };
